// src/features/counter/doctor-schedule/exportExcel.tsx
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

// รูปแบบข้อมูล
export interface CalendarDay {
    dateString: string;
    dayOfMonth: number;
    isCurrentMonth: boolean;
    isPreviousMonth?: boolean;
    isNextMonth?: boolean;
}
export interface ScheduleItem {
    doctorFullname: string;
    timeWorking: string;   // 'HH:mm - HH:mm'
    userTags: Array<{ color: string }>;
}
export interface ScheduleByDate {
    date: string;          // 'YYYY-MM-DD'
    schedule: ScheduleItem[];
}
export interface ExportParams {
    calendarGridDayObjects: CalendarDay[];
    dataSchedule: ScheduleByDate[];
    month: number;
    year: number;
    off: string,
    leave: string
}

const exportDoctorSchedule = async ({
    calendarGridDayObjects,
    dataSchedule,
    month,
    year,
    off,
    leave
}: ExportParams) => {
    const days = calendarGridDayObjects;
    const schedules = dataSchedule;

    // สร้าง Workbook + ปิด GridLines
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('ตารางแพทย์', {
        views: [{ showGridLines: false }]
    });

    // 1) Title (merge A1:G1)
    const titleText = `ตารางแพทย์ เดือน ${moment(`${year}-${month}-01`)
        .locale('th').format('MMMM YYYY')}`;
    sheet.mergeCells('A1:G1');
    Object.assign(sheet.getCell('A1'), {
        value: titleText,
        font: { bold: true, size: 16, color: { argb: 'FFFFFFFF' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0070C0' } }
    });
    sheet.getRow(1).height = 28;

    // 2) Header วัน (Row 2) พร้อมขอบครบด้าน
    const dayLabels = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์'];
    const headerRow = sheet.addRow(dayLabels);
    headerRow.eachCell(cell => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDEBF7' } };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    });
    sheet.getRow(2).height = 20;

    // 3) ตั้งความกว้างคอลัมน์
    const columnWidths = [40, 40, 40, 40, 40, 40, 40];
    columnWidths.forEach((w, idx) => {
        sheet.getColumn(idx + 1).width = w;
    });

    // 4) แบ่ง days เป็นสัปดาห์ละ 7 วัน
    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }

    // 5) แมป schedules เพื่อ lookup
    const schedMap: Record<string, ScheduleItem[]> = {};
    schedules.forEach(s => {
        schedMap[s.date] = s.schedule;
    });

    // 6) คำนวณ max events ต่อวัน
    const maxEventsPerDay = days.length
        ? Math.max(...days.map(d => (schedMap[d.dateString]?.length || 0)))
        : 0;

    // 7) วาดตารางสัปดาห์ละหนึ่งแถว (Row 3 เป็นต้นไป)
    let rowIndex = 3;
    weeks.forEach(weekDays => {
        // 7.1) แถวเลขวันที่ (ขอบครบด้าน)
        const dateRow = sheet.getRow(rowIndex++);
        dateRow.height = 18;
        weekDays.forEach((d, idx) => {
            const cell = dateRow.getCell(idx + 1);
            cell.value = d.dayOfMonth;
            cell.font = {
                bold: true,
                color: { argb: d.isCurrentMonth ? 'FF000000' : 'FFB0B0B0' }
            };
            cell.alignment = { horizontal: 'left', vertical: 'top' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // 7.2) แถวรายการ schedule (ชื่อ+เวลาในบรรทัดเดียว)  
        for (let evIdx = 0; evIdx < maxEventsPerDay; evIdx++) {
            const row = sheet.getRow(rowIndex++);
            row.height = 14;
            weekDays.forEach((d, idx) => {
                const cell = row.getCell(idx + 1);
                const evs = schedMap[d.dateString] || [];
                if (evIdx < evs.length) {
                    const ev: any = evs[evIdx];
                    const status = ev.statusDoctorSchedule === "NORMAL" ? ev.timeWorking : ev.statusDoctorSchedule === "MISSING" ? off : leave
                    cell.value = {
                        richText: [
                            { text: ev.doctorFullname },
                            {
                                text: " (" + status + ")",
                                font: {
                                    size: 11,
                                    bold: false,
                                    color:
                                        ev.statusDoctorSchedule === "NORMAL"
                                            ? { argb: "FF000000" }
                                            : ev.statusDoctorSchedule !== "MISSING"
                                                ? { argb: "FFE2A706" }
                                                : { argb: "FFFB5252" },
                                },
                            }
                        ],
                    };
                    cell.alignment = {
                        wrapText: true,
                        vertical: 'top',
                        horizontal: 'left'
                    };
                }
                // กำหนดเส้นขอบ ซ้าย–ขวา และ top/bottom เฉพาะขอบกลุ่ม
                const isFirst = evIdx === 0;
                const isLast = evIdx === maxEventsPerDay - 1;
                const borderDef: any = {
                    left: { style: 'thin' },
                    right: { style: 'thin' },
                };
                if (isFirst) borderDef.top = { style: 'thin' };
                if (isLast) borderDef.bottom = { style: 'thin' };
                cell.border = borderDef;
            });
        }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        `ตารางแพทย์_${month}_${year}.xlsx`
    );
};

export default exportDoctorSchedule;
